import type { ReactNode } from "react";
import { createContext, useContext, useEffect } from "react";

import { useArmStipulationCategoryFilter } from "../../../hooks/useArmStipulationCategoryFilter";
import type { SectionRegistrar } from "../../../hooks/useScrollSpy";
import { useScrollSpy } from "../../../hooks/useScrollSpy";
import type { ScrollSpySection } from "../../stipulations/stipulationCategoryFilters";
import { ALL_STIPULATION_CATEGORIES, SCROLL_SPY_SECTION_ORDER } from "../../stipulations/stipulationCategoryFilters";
import { useStipulationCategoryStore } from "../../stipulations/stipulationCategoryStore";

const noopRegistrar = () => {};

// Default lets sections render outside the provider (e.g. isolated tests) without tracking.
const ScrollSpyRegistrarContext = createContext<SectionRegistrar<ScrollSpySection>>(() => noopRegistrar);

/**
 * The scroll-spy's `registerSection` ref-callback factory for review sections.
 * @returns {SectionRegistrar<ScrollSpySection>} The registrar, or a no-op outside `ReviewScrollSpyProvider`.
 */
export function useScrollSpyRegistrar(): SectionRegistrar<ScrollSpySection> {
  return useContext(ScrollSpyRegistrarContext);
}

interface ReviewScrollSpyProviderProperties {
  className?: string;
  children: ReactNode;
}

/**
 * Renders the review sections' scroll container and drives the Stipulations
 * panel's category filter from whichever registered section is in the
 * activation band. Also arms the filter on the first real scroll or input
 * anywhere inside the container (see `useArmStipulationCategoryFilter`).
 * @param {ReviewScrollSpyProviderProperties} props - Container class name and the review sections.
 * @returns {ReactNode} The scroll container.
 */
export function ReviewScrollSpyProvider({ className, children }: ReviewScrollSpyProviderProperties) {
  const setCategoryFromScroll = useStipulationCategoryStore((state) => state.setCategoryFromScroll);

  // `undefined` means scrolled back above the first section → "All Stips".
  // Two columns in view (side by side) or none (a non-signer section) → "both".
  const { registerSection, resyncActiveSection } = useScrollSpy<ScrollSpySection>(
    SCROLL_SPY_SECTION_ORDER,
    (active) => {
      if (!active) {
        setCategoryFromScroll(ALL_STIPULATION_CATEGORIES, "both");
        return;
      }
      setCategoryFromScroll(active.category, active.applicants.length === 1 ? active.applicants[0] : "both");
    }
  );
  const registerContainerForArming = useArmStipulationCategoryFilter();

  // While pinned at the top, the store ignores scroll-spy reports (see `atTop`
  // in `stipulationCategoryStore`). The section active before the pin may still
  // be active once it releases, and its observer entry won't toggle again —
  // re-deliver it so the filter doesn't stay stuck on "All Stips".
  const atTop = useStipulationCategoryStore((state) => state.atTop);
  useEffect(() => {
    if (!atTop) {
      resyncActiveSection();
    }
  }, [atTop, resyncActiveSection]);

  return (
    <ScrollSpyRegistrarContext.Provider value={registerSection}>
      <div ref={registerContainerForArming} className={className}>
        {children}
      </div>
    </ScrollSpyRegistrarContext.Provider>
  );
}
